import { Component, OnInit } from '@angular/core';
import { DocumentStatus } from '@app/models/payroll/self-onboarding-document.model';
import { SelfOnboardingDocumentsService } from '@app/modules/payroll/services';
import { PAYROLL_SUPPORTING_DOCUMENTS } from '@app/modules/payroll/types/self-onboarding-document-type.type';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { AnalyticEvents } from '@app/enums';
import { Router } from '@angular/router';

@Component({
    templateUrl: './supporting-documents.template.html',
    styleUrls: ['../../../self-serve.style.scss'],
})
export class SupportingDocumentsView implements OnInit {
    isLoading = true;
    isPayrollSyncEnabled = false;
    isAllUploaded = false;

    /**
     * documents is the documents that will be shown on the page. We default to not signed
     * and add details as data is loaded in.
     */
    documents: Array<{ title: string; description: string; status: DocumentStatus; rejectionMessage: string }> = [
        {
            title: 'self-serve.payrollDocuments.payVerification',
            description: 'self-serve.payrollDocuments.payVerificationDescription',
            status: 'new',
            rejectionMessage: '',
        },

        {
            title: 'self-serve.payrollDocuments.placeAndHistoryOfBusiness',
            description: 'self-serve.payrollDocuments.placeAndHistoryOfBusinessDescription',
            status: 'new',
            rejectionMessage: '',
        },

        {
            title: 'self-serve.payrollDocuments.payorVerification',
            description: 'self-serve.payrollDocuments.payorVerificationDescription',
            status: 'new',
            rejectionMessage: '',
        },

        {
            title: 'self-serve.payrollDocuments.payorRelationship',
            description: 'self-serve.payrollDocuments.payorRelationshipDescription',
            status: 'new',
            rejectionMessage: '',
        },
    ];

    constructor(
        private analyticService: AnalyticService,
        private authService: AuthService,
        private notifyService: NotifyService,
        private router: Router,
        private selfOnboardingDocumentsService: SelfOnboardingDocumentsService
    ) {
        this.isPayrollSyncEnabled = this.authService.company.isPayrollSyncEnabled;
    }

    async ngOnInit(): Promise<void> {
        try {
            this.analyticService.trackEvent(AnalyticEvents.SelfServePayrollDocumentsClickSupportingDocumentsTab);

            if (!this.isPayrollSyncEnabled) {
                this.isLoading = false;
                return;
            }

            // load documents from payroll
            const documents = await this.selfOnboardingDocumentsService.getDocuments(PAYROLL_SUPPORTING_DOCUMENTS);

            const payVerification = {
                title: 'self-serve.payrollDocuments.payVerification',
                description: 'self-serve.payrollDocuments.payVerificationDescription',
                status: documents.PayVerification.state,
                rejectionMessage: documents.PayVerification.rejectionReason,
            };

            const placeAndHistoryOfBusiness = {
                title: 'self-serve.payrollDocuments.placeAndHistoryOfBusiness',
                description: 'self-serve.payrollDocuments.placeAndHistoryOfBusinessDescription',
                status: documents.PlaceAndHistoryOfBusiness.state,
                rejectionMessage: documents.PlaceAndHistoryOfBusiness.rejectionReason,
            };

            const payorVerification = {
                title: 'self-serve.payrollDocuments.payorVerification',
                description: 'self-serve.payrollDocuments.payorVerificationDescription',
                status: documents.PayorVerification.state,
                rejectionMessage: documents.PayorVerification.rejectionReason,
            };

            const payorRelationship = {
                title: 'self-serve.payrollDocuments.payorRelationship',
                description: 'self-serve.payrollDocuments.payorRelationshipDescription',
                status: documents.PayorRelationship.state,
                rejectionMessage: documents.PayorRelationship.rejectionReason,
            };

            this.documents = [payVerification, placeAndHistoryOfBusiness, payorVerification, payorRelationship];

            this.isAllUploaded = this.documents.every((doc) => doc.status !== 'new');

            this.isLoading = false;
        } catch (error) {
            this.notifyService.error(error);
        }
    }

    trackSelfServePayrollUploadDocuments(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServePayrollDocumentsClickViewSupportingDocuments);

        this.router.navigate(['/payroll', 'self-onboarding', 'supporting-documents']);
    }
}
